import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { hasAnyRole, isStaff } from "@/lib/roles";
import NoStaffAccess from "@/components/auth/NoStaffAccess";
import ForbiddenPanel from "@/components/auth/ForbiddenPanel";
import { FullPageSpinner } from "@/components/layout/PageSkeleton";

type RequireStaffProps = {
  children: ReactNode;
};

/**
 * Outer gate for the whole signed-in admin.
 * - No session → /admin/login.
 * - Session but no profile, zero roles, or only role 5 → "no staff access" page.
 *   (Zero roles used to pass every guard without requiredRoles.)
 */
export const RequireStaff = ({ children }: RequireStaffProps) => {
  const { user, session, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) return <FullPageSpinner label="Checking your access" />;

  if (!session) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  if (!user || !isStaff(user)) return <NoStaffAccess />;

  return <>{children}</>;
};

type AuthGuardProps = {
  children: ReactNode;
  /** Required. Every route names the roles that can open it (src/navigation/registry.ts). */
  requiredRoles: readonly number[];
};

/**
 * Per-route role check, rendered inside the persistent shell. A staff member
 * without the role sees an explanation in the content well instead of being
 * bounced somewhere else.
 */
const AuthGuard = ({ children, requiredRoles }: AuthGuardProps) => {
  const { user } = useAuth();

  if (!user || !isStaff(user)) return <NoStaffAccess />;

  if (!hasAnyRole(user, requiredRoles)) return <ForbiddenPanel requiredRoles={requiredRoles} />;

  return <>{children}</>;
};

export default AuthGuard;
